import type { SkuUsage } from '../data/analytics';
import type { ApiDetail, Endpoint, ProviderQuote } from '../features/catalog/model';

/** Cheapest provider quote for an endpoint, by worst-case cost per request. A
 *  metered lane's figure is its ceiling, so this no longer ranks a lane that can
 *  bill many times more than a flat one as the cheaper of the two. */
export const cheapest = (endpoint: Endpoint): ProviderQuote =>
  endpoint.providers.reduce((best, p) =>
    p.price.perRequestUsd < best.price.perRequestUsd ? p : best,
  );

/** Aggregate totals across an API's endpoints. */
export const apiTotals = (api: ApiDetail) => {
  const providers = new Set<string>();
  for (const ep of api.endpoints) {
    for (const p of ep.providers) providers.add(p.source.id);
  }
  return {
    endpoints: api.endpoints.length,
    providers: providers.size,
  };
};

// Counts add. The gateway combines both metric definitions over one trailing
// 30-day window before these figures reach the frontend, so every endpoint's
// number covers the same period: they are directly comparable, and a platform
// total is a plain SUM of them. That identity is the rule to protect here -
// nothing may pick a definition, reweight, or drop a row. (Rates are the
// opposite case: uptime and latency are not addable and still choose one
// definition, in lib/benchmarks.ts.)

/** An endpoint's real 30-day request count (0 when it has no SKU or no traffic). */
const endpointRequests = (ep: Endpoint, usage: Record<string, SkuUsage>): number =>
  usage[ep.sku]?.requests ?? 0;

/** An API's real 30-day request total: the sum of its endpoints' counts. */
export const apiRequests = (api: ApiDetail, usage: Record<string, SkuUsage>): number =>
  api.endpoints.reduce((n, e) => n + endpointRequests(e, usage), 0);

/** An API's endpoints ranked by real traffic, heaviest first. Original order is
 *  preserved wherever counts tie, so a young network keeps the catalog's own
 *  ordering. */
export const endpointOrder = (api: ApiDetail, usage: Record<string, SkuUsage>): Endpoint[] =>
  [...api.endpoints].sort((a, b) => endpointRequests(b, usage) - endpointRequests(a, usage));

/** Most-trafficked endpoint of an API; falls back to the first endpoint while
 *  the network is young. Drives the featured "From" price, so it must agree
 *  with the request total shown beside it. */
export const mostPopular = (api: ApiDetail, usage: Record<string, SkuUsage>): Endpoint =>
  api.endpoints.reduce((top, e) =>
    endpointRequests(e, usage) > endpointRequests(top, usage) ? e : top,
  );
